import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, isRef as _isRef, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "data-status-grid" }
const _hoisted_2 = {
  key: 0,
  class: "filter-description"
}
const _hoisted_3 = {
  key: 0,
  class: "btn-group ms-auto",
  style: {},
  role: "group"
}
const _hoisted_4 = ["href"]
const _hoisted_5 = ["href"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { key: 1 }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { key: 1 }

import BTable from "./BTable/BTable.vue";
import BTableHeader from "./BTable/BTableHeader.vue";
import {BTableColumn, BTableOptions} from "./BTable/BTable.types";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import {describeFilters, BFilterOptions, BFilter, BFilterableField, validateFilters} from "./BFilterEditor/BFilterEditor.types";

import BFilterModal from "./BFilterEditor/BFilterModal.vue";
import {handleErrorInResponse, getFiltersFromString, getSourceNumberFromText} from "@/utils";
import {debounce} from "vue-debounce";
import QueuePauseButton from "./QueuePauseButton.vue";
import {computed, ref, watch} from "vue";
import {UploadType} from "@/types";
import {useCommunityPolicingStore} from "@/stores/communityPolicingStore";
import {useContactsStore} from "@/stores/contactsStore";
import {useNibrsStore} from "@/stores/nibrsStore";
import {useUofStore} from "@/stores/uofStore";
import {useSummaryStore} from "@/stores/summaryStore";
import {usePoliceEmployeeStore} from "@/stores/policeEmployeeStore";
import {usePopulationStore} from "@/stores/populationStore";
import {AxiosResponse} from "axios";
import InformationModal from "./Misc/InformationModal.vue";
import DropDown from "@/components/Misc/DropDown.vue";
import { useDcraStore } from "@/stores/dcraStore";


export default /*@__PURE__*/_defineComponent({
  __name: 'DataUpload',
  props: {
    title: {},
    refreshRequired: {},
    dataset: {},
    onlyHasOneOri: { type: Boolean },
    showQueueStatus: { type: Boolean },
    submissionPeriodHidden: { type: Boolean },
    showWarningDetails: { type: Boolean },
    noTotal: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

var store = {} as any;
var uploadType:UploadType = UploadType[props.dataset ?? ""];

switch (uploadType) {
    case UploadType.contacts:
        store = useContactsStore();
        break;
    case UploadType.communityPolicing:
        store = useCommunityPolicingStore();
        break;
    case UploadType.nibrs:
        store = useNibrsStore();
        break;
    case UploadType.summary:
        store = useSummaryStore();
        break;
    case UploadType.useOfForce:
        store = useUofStore();
        break;    
    case UploadType.policeEmployee:
        store = usePoliceEmployeeStore();
        break;
    case UploadType.population:
        store = usePopulationStore();
        break;
    case UploadType.dcra:
        store = useDcraStore();
        break;
}

//What would have gone under data: {...}
var dataItems = ref([] as any[]);
const columns = store.getFileUploadColumns(props.onlyHasOneOri, props.submissionPeriodHidden);
var options = ref({
    totalItemCount: 0,
    itemsPerPage: 25,
    page: 1,
    sortBy: [
        {
            key: "uploadDate",
            isDescending: true,
        },
    ],
    pagingMode: props.noTotal ? "noTotal" : "withTotal",
} as BTableOptions);
var showLoading = ref(false);
var initialLoad = ref(true);

var isShowFilterEditor = ref(false);
var hasAppliedFilters = ref(false);
var filterOptions = ref({
    filters: [],
} as BFilterOptions);
var total = ref(0);
var updateTableDataSource = debounce(() => getData(), "10000ms");
var showDownloadErrorModal = ref(false);
var downloadErrorBody = ref("An error was encountered while trying to download the file you requested.");

var filtersDescription = ref("");

//Computed properties
const result = computed(() => {
    return dataItems.value;
});

var filterableFields = computed((): BFilterableField[] => {
    return store.getFilterableFieldsForFileUploads(
        props.onlyHasOneOri,
        props.submissionPeriodHidden
    ) as BFilterableField[];
});

function setDescription() {
    if (filterOptions == null) {
        return "";
    }
    hasAppliedFilters.value = filterOptions.value.filters.length > 0;

    return describeFilters(filterOptions.value.filters, filterableFields.value);
}

//Get values from query string
const params: Record<string, any> = new URLSearchParams(window.location.search);

if (params.has("pagenumber") || params.has("pagesize") || params.has("filter") || params.has("sort")) {
    let pagenumber = params.pagenumber; // "some_value"
    let pagesize = params.pagesize;
    let filters = params.filter;
    let sort = params.sort;

    if (pagenumber > 1) {
        options.value.page = parseInt(pagenumber);
    }

    if (pagesize != undefined) {
        options.value.itemsPerPage = parseInt(pagesize);
    }

    if (sort != undefined && sort.length > 0) {
        let isDescending = sort.toString().indexOf("-") === 0;
        let sortBy = [
            {
                key: sort.toString().substring(1),
                isDescending: isDescending,
            },
        ];
        options.value.sortBy = sortBy;
    }

    if (filters != undefined && filters.length > 0) {
        filterOptions.value = getFiltersFromString(filters);
    }
} else {
    //Saved table options
    let optionsJson = sessionStorage.getItem(`du_${uploadType}_tableOptions`);
    if (optionsJson) {
        options.value = JSON.parse(optionsJson) as BTableOptions;
    }

    //filters
    let filtersJson = sessionStorage.getItem(`du_${uploadType}_filterOptions`);
    if (filtersJson) {
        filterOptions.value = JSON.parse(filtersJson) as BFilterOptions;
    }
}

if (filterOptions.value.filters.length > 0) {
    applyFilters(filterOptions.value);
} else {
    getData();
}

initialLoad.value = false;

function getData(autoRefresh: boolean = false) {
    if (isShowFilterEditor.value) {
        return;
    }
    
    showLoading.value = true;
    options.value.pagingMode = props.noTotal ? "noTotal" : "withTotal";

    store?.getFiles(filterOptions.value.filters, options.value, filterableFields.value, options.value.sortBy)
        .then((response: AxiosResponse) => {
            if (options.value.pagingMode == "noTotal" && response?.data?.data?.length > 0) {
                //When were hiding the total text,first button, and last button then we return itemsPerPage + 1 in our get data call.
                //If the returned amount of rows is greater than itemsPerPage then enable the next button otherwise disable the next button to prevent an error
                dataItems.value = response.data.data.slice(0, options.value.itemsPerPage);
            } else {
                dataItems.value = response.data.data;
            }

            total.value = response.data.total;
            options.value.totalItemCount = response.data.total;
            if (dataItems.value.findIndex((item) => item.status === "Pending" || item.status == "In progress") >= 0) {
                autoRefresh = true;
            }
        })
        .catch(function (error) {
            handleErrorInResponse(error);
        })
        .finally(() => {
            showLoading.value = false;
            if (autoRefresh) {
                updateTableDataSource();
            }
        });
}

function applyFilters(newFilterOptions: BFilterOptions) {
    if(!validateFilters(newFilterOptions.filters)){
        return;
    }

    filterOptions.value = newFilterOptions;
    isShowFilterEditor.value = false;

    if (hasAppliedFilters.value) {
        options.value.page = 1;
    }

    hasAppliedFilters.value = filterOptions.value.filters.length > 0;
    filtersDescription.value = describeFilters(filterOptions.value.filters, filterableFields.value);

    getData();
}

function resetFilters() {
    if (filterOptions.value != null) {
        filterOptions.value.filters = [];
        options.value.page = 1;
    }

    isShowFilterEditor.value = false;
    hasAppliedFilters.value = false;
    filtersDescription.value = "";

    getData();
}

function showFilterEditor(event: any) {
    event.preventDefault();

    isShowFilterEditor.value = true;
}

function handleOptionChange(newOptions) {
    if (newOptions != null) {
        options.value = newOptions;
        getData();
    }
}

function getDetailsUrl(id: number): string {
    if (id > 0) {
        return `uploaddetails.aspx?id=${id}&source=${getSourceNumberFromText(props.dataset as string)}`;
    }
    return "";
}

function isStatusLinkEnabled(item: any): boolean {
    return item.id > 0;
}

function redirectToUrl(url: string) {
    window.location.href = url;
}

function getDownloadUrl(item: any) {
    if (!item.fileName || !item.downloadUrl) {
        return "";
    }
    return item.downloadUrl;
}

function handleDownloadErrorModalDismiss() {
    showDownloadErrorModal.value = false;
}

function downloadFileHopefully(downloadUrl: string, fileName: string) {
    try {
        checkIfDownloadValid(downloadUrl)
            .then((result) => {
                if (result) {
                    window.open(downloadUrl, "_blank");
                } else {
                    downloadErrorBody.value = `An error was encountered while trying to download: ${fileName}`;
                    showDownloadErrorModal.value = true;
                }
            })
            .catch((error) => {
                downloadErrorBody.value = `An error was encountered while trying to download: ${fileName}`;
                showDownloadErrorModal.value = true;
            });
    } catch (error) {
        downloadErrorBody.value = `An error was encountered while trying to download: ${fileName}`;
        showDownloadErrorModal.value = true;
    }
}

async function checkIfDownloadValid(downloadUrl: string): Promise<boolean> {
    let result = false;

    if (downloadUrl != null && downloadUrl != "") {
        result = await store.checkIfDownloadValid(downloadUrl);
    }

    return result;
}

watch(
    () => options,
    (newOptions) => {
        sessionStorage.setItem(`du_${uploadType}_tableOptions`, JSON.stringify(options.value));
        if (!initialLoad) getData();
    },
    {deep: true}
);

watch(
    () => filterOptions,
    (newFilters) => {
        sessionStorage.setItem(`du_${uploadType}_filterOptions`, JSON.stringify(filterOptions.value));
    },
    {deep: true}
);

watch(
    () => props.refreshRequired,
    (val) => {
        getData(true);
    }
);

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LoadingSpinner, { "show-loading": _unref(showLoading) }, null, 8, ["show-loading"]),
    _createVNode(BFilterModal, {
      modelValue: _unref(isShowFilterEditor),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(isShowFilterEditor) ? (isShowFilterEditor).value = $event : isShowFilterEditor = $event)),
      filterOptions: _unref(filterOptions),
      filterableFields: _unref(filterableFields),
      onFilterChanged: applyFilters
    }, null, 8, ["modelValue", "filterOptions", "filterableFields"]),
    _createVNode(InformationModal, {
      title: "Unable to download file",
      body: _unref(downloadErrorBody),
      modelValue: _unref(showDownloadErrorModal),
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(showDownloadErrorModal) ? (showDownloadErrorModal).value = $event : showDownloadErrorModal = $event))
    }, null, 8, ["body", "modelValue"]),
    _createVNode(BTableHeader, {
      value: _unref(options),
      onOptionChange: handleOptionChange,
      "header-slot-class": "b-header-slot w-100",
      "current-item-count": _unref(total)
    }, {
      default: _withCtx(() => [
        _createElementVNode("button", {
          onClick: _cache[2] || (_cache[2] = ($event: any) => (showFilterEditor($event))),
          class: "btn btn-primary filter-button-width"
        }, _cache[4] || (_cache[4] = [
          _createElementVNode("i", { class: "bi bi-search pe-1" }, null, -1),
          _createTextVNode(" Filter ")
        ])),
        _renderSlot(_ctx.$slots, "header.toolbar"),
        (_unref(hasAppliedFilters))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("span", null, _toDisplayString(_unref(filtersDescription)), 1),
              _withDirectives(_createElementVNode("button", {
                class: "btn-close",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (resetFilters()))
              }, null, 512), [
                [_directive_tooltip, 'Remove all currently applied filters.']
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      headerTopBar: _withCtx(() => [
        _createVNode(QueuePauseButton, {
          "show-pause-resume-button": _ctx.showQueueStatus,
          class: ""
        }, null, 8, ["show-pause-resume-button"]),
        (props.showWarningDetails)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(DropDown, {
                label: "Error/Warning Details",
                "item-count": 2,
                "is-primary-button": false
              }, {
                item1: _withCtx(({}) => [
                  _createElementVNode("a", {
                    class: "dropdown-item",
                    href: `${_unref(store).errorDescriptionsDownloadUrl}csv`
                  }, "Download as .csv", 8, _hoisted_4)
                ]),
                item2: _withCtx(({}) => [
                  _createElementVNode("a", {
                    class: "dropdown-item",
                    href: `${_unref(store).errorDescriptionsDownloadUrl}xlsx`
                  }, "Download as .xlsx", 8, _hoisted_5)
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 3
    }, 8, ["value", "current-item-count"]),
    _createVNode(BTable, {
      items: result.value,
      columns: _unref(columns),
      caption: 'List of errors and warnings',
      value: _unref(options),
      onOptionChange: handleOptionChange,
      class: "mt-1"
    }, {
      "cell.fileName": _withCtx(({item, column}) => [
        (item.downloadUrl != null && item.downloadUrl != '')
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-link ms-0 ps-0 text-start",
              onClick: _withModifiers(($event: any) => (downloadFileHopefully(item.downloadUrl, item.fileName)), ["prevent"])
            }, _toDisplayString(item.fileName), 9, _hoisted_6))
          : (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(item.fileName), 1))
      ]),
      "cell.status": _withCtx(({item}) => [
        (isStatusLinkEnabled(item))
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-link ms-0 ps-0 text-start",
              onClick: _withModifiers(($event: any) => (redirectToUrl(getDetailsUrl(item.id))), ["prevent"])
            }, _toDisplayString(item.status), 9, _hoisted_8))
          : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(item.status), 1))
      ]),
      _: 1
    }, 8, ["items", "columns", "value"]),
    _createVNode(BTableHeader, {
      value: _unref(options),
      onOptionChange: handleOptionChange,
      "header-slot-class": "b-header-slot w-100",
      "current-item-count": _unref(total)
    }, null, 8, ["value", "current-item-count"])
  ]))
}
}

})